'use client';

import { useEffect, useRef } from 'react';
import { useCheckoutStore, selectIsCheckoutOpen } from '@/stores/checkout-store';
import { useAddressStore } from '@/stores/address-store';
import { captureEvent } from '@/lib/source-tracker';

/**
 * Наблюдатель за checkout — раз за сессию шлёт ключевые события:
 *   - address_entered       — когда выбран валидный адрес
 *   - delivery_cost_viewed  — когда показалась стоимость доставки
 *   - phone_entered         — когда введён валидный 11-значный телефон
 *   - name_entered          — когда введено непустое имя
 *
 * Каждое событие отправляется ОДИН раз за сессию (через useRef-флаги),
 * чтобы не спамить onChange'ами.
 *
 * ВАЖНО про регидратацию: contact и selectedAddress персистятся в localStorage
 * (zustand persist), а <CartSheet/> смонтирован на каждой странице. Без защиты
 * у любого вернувшегося посетителя события уходили бы на первой же странице,
 * при закрытой корзине — то есть без всякого пользовательского ввода.
 * Поэтому значение, поднятое из localStorage на маунте, считается «не вводом»:
 * оно шлётся только когда корзина реально открыта, и с признаком restored=true,
 * чтобы воронка могла отделить его от честного заполнения формы.
 */
export function useCheckoutEventTracker() {
  const contact = useCheckoutStore((s) => s.contact);
  const isCheckoutOpen = useCheckoutStore(selectIsCheckoutOpen);
  const selectedAddress = useAddressStore((s) => s.selectedAddress);

  const phoneTrackedRef = useRef(false);
  const nameTrackedRef = useRef(false);
  const addressTrackedRef = useRef(false);
  const deliveryCostTrackedRef = useRef(false);

  // Снимок значений на маунте — это то, что persist поднял из localStorage.
  //
  // Читаем через getState(), а НЕ из значений подписки выше: zustand v5 отдаёт
  // в useSyncExternalStore getInitialState() (состояние ДО регидратации) на
  // рендере гидратации, поэтому снимок «из пропсов хука» получился бы пустым —
  // и любое персистнутое значение выглядело бы как свежий ввод пользователя.
  // getState() отдаёт актуальное состояние: persist поднимает localStorage
  // синхронно при создании стора, то есть ещё до первого рендера.
  const restoredRef = useRef<{ phone: string; name: string; address: string } | null>(null);
  if (restoredRef.current === null) {
    const persistedContact = useCheckoutStore.getState().contact;
    const persistedAddress = useAddressStore.getState().selectedAddress;
    restoredRef.current = {
      phone: (persistedContact?.phone || '').replace(/\D/g, ''),
      name: (persistedContact?.name || '').trim(),
      address: (persistedAddress?.address || '').trim(),
    };
  }

  // ── phone_entered ── (ВАЖНО: создаёт горячий лид)
  useEffect(() => {
    if (phoneTrackedRef.current) return;
    const digits = (contact.phone || '').replace(/\D/g, '');
    if (digits.length === 11) {
      const restored = digits === restoredRef.current?.phone;
      if (restored && !isCheckoutOpen) return;   // номер из localStorage при закрытой корзине — не событие
      phoneTrackedRef.current = true;
      captureEvent('phone_entered', {
        phone: '+' + digits,                    // полный номер для leads
        phone_masked: '+' + digits.slice(0, 4) + '***' + digits.slice(-2),
        has_name: !!contact.name,
        restored,
      });
    }
  }, [contact.phone, contact.name, isCheckoutOpen]);

  // ── name_entered ──
  useEffect(() => {
    if (nameTrackedRef.current) return;
    if (contact.name && contact.name.trim().length >= 2) {
      const restored = contact.name.trim() === restoredRef.current?.name;
      if (restored && !isCheckoutOpen) return;
      nameTrackedRef.current = true;
      captureEvent('name_entered', { name_length: contact.name.length, restored });
    }
  }, [contact.name, isCheckoutOpen]);

  // ── address_entered ──
  // Срабатывает только когда есть selectedAddress: в address-store стоит
  // валидация (строка 244), которая не позволяет сохранить адрес без номера
  // дома → к этому моменту у нас гарантированно ПОЛНЫЙ адрес (улица + дом).
  useEffect(() => {
    if (addressTrackedRef.current) return;
    if (!selectedAddress?.address || selectedAddress.address.trim().length <= 5) return;

    const restored = selectedAddress.address.trim() === restoredRef.current?.address;
    if (restored && !isCheckoutOpen) return;   // адрес из localStorage при закрытой корзине — не событие

    addressTrackedRef.current = true;
    captureEvent('address_entered', {
      // Значение поднято из localStorage, а не введено сейчас.
      restored,
      // Полный адрес (для аналитики, доступен только админам).
      address: selectedAddress.address,
      // Координаты (если резолвились) — для будущей геоаналитики.
      lat: selectedAddress.coordinates?.lat ?? null,
      lon: selectedAddress.coordinates?.lon ?? null,
      // Резолвится ли в нашу зону доставки.
      is_in_zone: selectedAddress.isInZone,
      // Зона доставки — null если адрес вне зоны.
      zone_id: selectedAddress.deliveryZone?.id ?? null,
      delivery_point_id: selectedAddress.deliveryPointId ?? null,
      delivery_cost: selectedAddress.deliveryCost ?? null,
      min_order_sum: selectedAddress.minOrderSum ?? null,
      estimated_time: selectedAddress.estimatedTime ?? null,
      // Длина оставляем для обратной совместимости с прошлыми отчётами.
      address_length: selectedAddress.address.length,
    });
  }, [selectedAddress, isCheckoutOpen]);

  // ── delivery_cost_viewed ──
  // Срабатывает когда зона определена и стоимость доставки реально показана UI.
  useEffect(() => {
    if (deliveryCostTrackedRef.current) return;
    const zone = selectedAddress?.deliveryZone;
    if (!zone?.id || !selectedAddress?.isInZone) return;

    // Стоимость доставки видна только в открытой корзине: пока она закрыта,
    // адрес из localStorage событием не считается.
    const restored = (selectedAddress.address || '').trim() === restoredRef.current?.address;
    if (restored && !isCheckoutOpen) return;

    deliveryCostTrackedRef.current = true;
    captureEvent('delivery_cost_viewed', {
      restored,
      // Контекст того же события для drill-down.
      address: selectedAddress.address,
      zone_id: zone.id,
      delivery_point_id: selectedAddress.deliveryPointId ?? null,
      delivery_cost: selectedAddress.deliveryCost ?? null,
      min_order_sum: selectedAddress.minOrderSum ?? null,
      estimated_time: selectedAddress.estimatedTime ?? null,
    });
  }, [selectedAddress, isCheckoutOpen]);
}
